/**
 * React's view of a realtime conversation.
 *
 * ── THE HOOK OWNS NO PROTOCOL ──────────────────────────────────────────────────
 *
 * RealtimeClient owns the socket, the worklets, the acknowledgements and the
 * interruption path. This hook subscribes to it and turns what it reports into
 * render state — nothing more. That split is not tidiness: barge-in has to flush
 * audio without waiting for a render, and microphone frames leave on the audio
 * clock's schedule. Putting either behind setState would add a render's latency
 * to something measured in tens of milliseconds.
 *
 * ── CLEANUP IS NOT OPTIONAL ────────────────────────────────────────────────────
 *
 * Leaving this mode must release the microphone. A live recording indicator that
 * outlives the conversation is a genuine privacy failure, so teardown runs on
 * unmount as well as on an explicit end.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RealtimeClient, type RealtimeUiState } from './RealtimeClient';

export interface TranscriptEntry {
  id: string;
  from: 'user' | 'assistant';
  text: string;
}

export interface UseRealtimeConversationOptions {
  wsUrl: string;
  token: string | null | undefined;
  /** Only start when the SERVER says both capabilities are present. */
  enabled: boolean;
}

export function useRealtimeConversation(opts: UseRealtimeConversationOptions) {
  const clientRef = useRef<RealtimeClient | null>(null);
  const [state, setState] = useState<RealtimeUiState>('idle');
  const [partial, setPartial] = useState('');
  const [entries, setEntries] = useState<TranscriptEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [micGranted, setMicGranted] = useState<boolean | null>(null);
  /** Assistant text accumulates per response; committed when it completes. */
  const assistantDraft = useRef('');

  /**
   * Start talking in a spoken attempt.
   *
   * The session id is an ARGUMENT, not an option read at render time: the
   * attempt is created at the moment the user presses Start, so the id does not
   * exist until then, and reading it back from React state in the same tick
   * would see the previous (or no) attempt.
   */
  const start = useCallback(async (sessionId: string) => {
    if (!opts.enabled || !opts.token || !sessionId || clientRef.current) return;
    setError(null);

    const client = new RealtimeClient({
      url: opts.wsUrl,
      token: opts.token,
      sessionId,
      handlers: {
        state: (s) => {
          setState(s);
          // A finished or interrupted reply is committed to history here, where
          // the draft is known to be complete.
          if ((s === 'listening' || s === 'completed') && assistantDraft.current) {
            const text = assistantDraft.current;
            assistantDraft.current = '';
            setEntries((prev) => [
              ...prev,
              { id: `a-${prev.length}-${text.length}`, from: 'assistant', text },
            ]);
          }
        },
        partialTranscript: (t) => setPartial(t),
        finalTranscript: (t) => {
          setPartial('');
          setEntries((prev) => [...prev, { id: `u-${prev.length}`, from: 'user', text: t }]);
        },
        assistantText: (t) => { assistantDraft.current += t; },
        error: (code) => setError(code),
        micPermission: (granted) => setMicGranted(granted),
      },
    });

    clientRef.current = client;
    await client.start();
  }, [opts.enabled, opts.token, opts.wsUrl]);

  const end = useCallback(async () => {
    const client = clientRef.current;
    clientRef.current = null;
    await client?.stop();
    setState('completed');
    setPartial('');
  }, []);

  // Unmount must release the microphone too — navigating away is the most
  // likely way to leave, and a live mic indicator must not survive it.
  useEffect(() => {
    return () => {
      const client = clientRef.current;
      clientRef.current = null;
      void client?.stop();
    };
  }, []);

  return {
    state,
    partial,
    entries,
    error,
    micGranted,
    /** Live text from the reply currently being spoken. */
    assistantPartial: assistantDraft.current,
    start,
    end,
    active: clientRef.current !== null,
  };
}
